import { DialogTitle } from '@aibees/shared/components';
import { classnames, fCredit, fNumber } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, ReactNode } from 'react';
import { InformNotificationPopup } from '../inform-notification-popup';
import * as Styled from './styles';
import * as SX from './sx';
import type { BulkUpdateDialogProps as Props } from './types';

const Calculating = () => <CircularProgress size="12px" sx={SX.progress} />;

const Unavailable = () => (
  <Typography variant="body3" color="grey.500">
    —
  </Typography>
);

/**
 * Preflight for the Saved Records bulk update actions. Has three states —
 * Calculating, Ready and Failed — and closes on submit; running and completion
 * feedback belongs to the top product notification, not to this dialog.
 *
 * See docs/saved-records-enrich-actions.md.
 */
export const BulkUpdateDialog = memo(
  ({
    className,
    open,
    onClose,
    icon,
    title,
    selectedCount,
    rows,
    skipped,
    totalCost,
    isCalculating,
    failed,
    disableSubmit,
    disabledHelperText,
    onSubmit,
  }: Props) => {
    // Calculating wins over Failed: a retry is already in flight, so the spinner
    // is the truer thing to show.
    const renderValue = (value: ReactNode) =>
      isCalculating ? <Calculating /> : failed ? <Unavailable /> : value;

    return (
      <Styled.Wrapper
        className={classnames('BulkUpdateDialog', className)}
        sx={SX.root}
        maxWidth="md"
        fullWidth
        open={open}
        onClose={onClose}
      >
        <Box>
          <DialogTitle title={title} icon={icon} onClose={onClose} />

          <DialogContent>
            <Typography color="grey.700" variant="body2" fontWeight={500}>
              You selected{' '}
              <Box component="span" color="grey.700" fontWeight="bold">
                {fNumber(selectedCount)}
              </Box>{' '}
              records.
            </Typography>

            {failed && !isCalculating && (
              <Alert severity="error" variant="outlined" sx={SX.error}>
                <Typography variant="body4" fontWeight={400} color="grey.600">
                  We couldn&apos;t calculate the cost for this update. Close
                  this dialog and try again — no credits have been spent.
                </Typography>
              </Alert>
            )}

            {/* Rates are known up-front, so they render while eligibility is
                still being calculated. */}
            <Table size="small" sx={SX.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Update type</TableCell>
                  <TableCell sx={SX.numericCell}>Contacts</TableCell>
                  <TableCell sx={SX.numericCell}>Rate</TableCell>
                  <TableCell sx={SX.numericCell}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>
                      <Box sx={SX.updateTypeCell}>
                        <Typography variant="body3" color="grey.700">
                          {row.label}
                        </Typography>
                        {row.tooltip && (
                          <InformNotificationPopup>
                            {row.tooltip}
                          </InformNotificationPopup>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={SX.numericCell}>
                      {renderValue(
                        <Typography variant="body3" color="grey.700">
                          {fNumber(row.count)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={SX.numericCell}>
                      <Typography variant="body3" color="grey.600">
                        {fCredit(row.rate)} credits
                      </Typography>
                    </TableCell>
                    <TableCell sx={SX.numericCell}>
                      {renderValue(
                        <Typography variant="body3" color="grey.700">
                          {fCredit(row.subtotal)} credits
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={SX.summaryRow}>
              <Typography variant="body3" color="grey.600">
                Skipped contacts
              </Typography>
              {renderValue(
                <Typography variant="body3" color="grey.700" fontWeight="bold">
                  {fNumber(skipped ?? 0)}
                </Typography>
              )}
            </Box>

            <Box sx={SX.totalRow}>
              <Typography variant="body3" color="primary.500">
                Estimated total cost
              </Typography>
              {renderValue(
                <Typography
                  variant="body3"
                  color="primary.500"
                  fontWeight="bold"
                >
                  {fCredit(totalCost ?? 0)} credits
                </Typography>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              variant="text"
              color="default"
              size="small"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Tooltip
              title={
                disableSubmit && !isCalculating ? disabledHelperText : undefined
              }
            >
              <Box>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={onSubmit}
                  loading={isCalculating}
                  disabled={disableSubmit}
                >
                  Update
                </LoadingButton>
              </Box>
            </Tooltip>
          </DialogActions>
        </Box>
      </Styled.Wrapper>
    );
  }
);

BulkUpdateDialog.displayName = 'BulkUpdateDialog';
